// Fase A1 · dónde vive la ventana del segundo factor.
//
// ── POR QUÉ NO EN ESTADO DE REACT ─────────────────────────────────────────────────────────
//
// La primera versión la guardaba con `useState` dentro del hook, y eso la ataba al componente:
// el listado tenía la suya y la ficha la suya. Al navegar del listado a una ficha,
// `GestionPersonasPersonas` se desmontaba y la ventana se iba con él, así que había que volver
// a teclear el código aunque acabaras de pasar la puerta.
//
// Y estaba mal de origen, no le faltaba una función: **la ventana es de quien mira, no de la
// pantalla**. Abrirla una vez tiene que servir para el listado y para el Soporte de cuantas
// fichas haga falta, que es lo que el servidor ya hacía — acepta el mismo token en las dos
// superficies y para varias personas.
//
// ── POR QUÉ localStorage, Y NO sessionStorage (15/10) ─────────────────────────────────────
//
// Hasta el 15/10 vivía en sessionStorage, que es POR PESTAÑA: Sistemas abría una pestaña nueva y
// el candado salía cerrado aunque la otra tuviera la ventana abierta. Y la razón que se dio para
// guardarla en el navegador vale igual aquí: quien puede leer este almacén **ya tiene el token de
// sesión al lado**, que vive en localStorage. Así que compartirla entre pestañas no abre una
// superficie nueva; sólo deja de pedir el código otra vez por abrir una pestaña.
//
// Tres condiciones que no son opcionales:
//
//   1. **Al agotarse se BORRA, no se ignora.** Un token caducado ahí es basura que confunde a
//      quien mire el almacén y a la propia pantalla.
//   2. **Va ATADA A LA SESIÓN con la que se abrió.** La sesión se borra en nueve sitios distintos
//      (salir, sesión caducada, un 401...) y sólo uno limpiaba también la ventana. Con
//      sessionStorage daba igual —moría con la pestaña—; en localStorage sobreviviría a los otros
//      ocho, y quien entrara después en ese navegador vería el candado abierto. El servidor la
//      rechazaría igual (exige que la ventana sea del mismo usuario), pero el candado mentiría. Así
//      que se guarda una huella del token de sesión, y una ventana de otra sesión no vale: sin
//      depender de acordarse de los nueve sitios.
//   3. **Las demás pestañas se enteran.** El evento nativo `storage` llega a las OTRAS pestañas
//      cuando una cambia la ventana o la sesión; el evento propio, a la misma.

const CLAVE = 'intranet.ventana2fa';

// La de antes vivía en sessionStorage con la misma clave. Se borra para que no quede un resto.
try { sessionStorage.removeItem(CLAVE); } catch { /* sin almacén */ }

// El candado de la barra y la pantalla donde se pasa la puerta son componentes distintos, y el
// almacén no avisa en la misma pestaña cuando cambia. Un evento propio los mantiene en el mismo
// estado.
const EVENTO = 'intranet:ventana2fa';
const avisar = () => {
  try { window.dispatchEvent(new CustomEvent(EVENTO)); } catch { /* sin DOM */ }
};

/** Huella del token de sesión (FNV-1a). No protege nada: sólo dice «es la misma sesión». */
const huellaSesion = () => {
  let t = null;
  try { t = localStorage.getItem('token'); } catch { return null; }
  if (!t) return null;
  let h = 0x811c9dc5;
  for (let i = 0; i < t.length; i += 1) { h ^= t.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16);
};

/**
 * Suscribe a los cambios de la ventana: los de esta pestaña (evento propio) y los de las demás
 * (evento `storage`, también cuando cambia la sesión). Devuelve la función para darse de baja.
 */
export const alCambiarVentana = (fn) => {
  const deOtraPestana = (e) => { if (e.key === null || e.key === CLAVE || e.key === 'token') fn(); };
  window.addEventListener(EVENTO, fn);
  window.addEventListener('storage', deOtraPestana);
  return () => {
    window.removeEventListener(EVENTO, fn);
    window.removeEventListener('storage', deOtraPestana);
  };
};

/**
 * Guarda la ventana recién abierta o prorrogada. `expiraEnIso` y lo de `extra` (`prorrogable`,
 * `tope`) los manda el servidor: la pantalla no decide cuánto dura ni si se puede alargar.
 */
export const guardarVentana = (token, expiraEnIso, minutos, extra = {}) => {
  if (!token) return;
  const expira = expiraEnIso ? new Date(expiraEnIso).getTime() : Date.now() + 5 * 60 * 1000;
  try {
    localStorage.setItem(CLAVE, JSON.stringify({
      token, expira, minutos: minutos || null,
      prorrogable: !!extra.prorrogable,
      tope: extra.tope ? new Date(extra.tope).getTime() : null,
      sesion: huellaSesion(),
    }));
    avisar();
  } catch {
    // Almacén lleno o bloqueado: se pierde la comodidad, no el acceso — se vuelve a pedir el código.
  }
};

/**
 * La ventana viva, o null.
 *
 * La BORRA antes de devolver null si ha caducado o es de otra sesión: las condiciones 1 y 2.
 * Leerla es también el momento natural de limpiarla, porque es cuando se sabe que sobra.
 */
export const leerVentana = () => {
  let crudo;
  try {
    crudo = localStorage.getItem(CLAVE);
  } catch {
    return null;
  }
  if (!crudo) return null;
  let dato;
  try {
    dato = JSON.parse(crudo);
  } catch {
    limpiarVentana();
    return null;
  }
  if (!dato || !dato.token || typeof dato.expira !== 'number') {
    limpiarVentana();
    return null;
  }
  if (Date.now() >= dato.expira || !dato.sesion || dato.sesion !== huellaSesion()) {
    limpiarVentana();
    return null;
  }
  return dato;
};

/** Borra la ventana. Se llama al caducar y AL CERRAR SESIÓN, y llega a todas las pestañas. */
export const limpiarVentana = () => {
  try {
    if (localStorage.getItem(CLAVE) === null) return;
    localStorage.removeItem(CLAVE);
    avisar();
  } catch {
    // nada que limpiar o almacén no accesible.
  }
};
